import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { servicesApi, pagesApi } from "../services/api";
import HeroSection from "../components/HeroSection";

function CoachingPage() {
  const [coachingOptions, setCoachingOptions] = useState([]);
  const [pageData, setPageData] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCoachingData();
  }, []);

  const fetchCoachingData = async () => {
    setLoading(true);
    try {
      const [optionsRes, testimonialsRes, pageDataRes] = await Promise.all([
        servicesApi.getCoachingOptions(),
        servicesApi.getCoachingTestimonials(),
        pagesApi.getByPageType("coaching"),
      ]);

      setPageData(pageDataRes.data || []);

      const options = optionsRes.data || [];
      setCoachingOptions(
        options.map((opt) => ({
          ...opt,
          features: opt.features ? opt.features.split(",") : [],
        })),
      );
      setTestimonials(testimonialsRes.data || []);
    } catch (error) {
      console.error("Error loading coaching data:", error);
      setFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const setFallbackData = () => {
    setCoachingOptions([
      {
        title: "1:1 Executive Coaching",
        description: "Personalized coaching for senior leaders and executives",
        features: [
          "Weekly 60-minute sessions",
          "Personalized development plan",
          "360-degree feedback analysis",
          "Progress tracking and accountability",
        ],
        duration: "3-6 months",
        icon: "👨‍💼",
      },
      {
        title: "Career Advancement Coaching",
        description:
          "For professionals looking to accelerate their career growth",
        features: [
          "Career path planning",
          "Interview preparation",
          "Networking strategies",
          "Personal branding",
        ],
        duration: "2-4 months",
        icon: "📈",
      },
      {
        title: "Group Masterclasses",
        description: "Interactive group sessions on specific leadership topics",
        features: [
          "Small group setting (max 10 people)",
          "Peer learning and networking",
          "Live Q&A sessions",
          "Practical exercises and case studies",
        ],
        duration: "4-8 weeks",
        icon: "👥",
      },
    ]);

    setTestimonials([
      {
        name: "Sarah Johnson",
        role: "Product Director",
        company: "Tech Solutions Inc.",
        content:
          "The coaching sessions transformed my leadership approach. I'm now more confident in making strategic decisions.",
        rating: 5,
      },
      {
        name: "Michael Chen",
        role: "Entrepreneur",
        company: "StartUp Ventures",
        content:
          "Mohammad's guidance helped me navigate the challenges of scaling my business. Highly recommended!",
        rating: 5,
      },
    ]);
  };

  useEffect(() => {
    if (pageData?.use_slider && pageData?.slider_images?.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % pageData.slider_images.length);
      }, pageData.slider_interval || 5000);
      return () => clearInterval(interval);
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }
  return (
    <div>
      <HeroSection
        title={
          pageData?.hero_headline ||
          pageData?.title ||
          "Become a better leader, transform your business"
        }
        subtitle={
          pageData?.hero_subheadline ||
          pageData?.meta_description ||
          pageData?.content ||
          "Turn people into assets, drive growth with innovation"
        }
        heroImage={pageData?.hero_image}
        useSlider={!!pageData?.use_slider}
        sliderImages={pageData?.slider_images || []}
        currentSlide={currentSlide}
        onSlideChange={setCurrentSlide}
        ctaText={pageData?.cta_text}
        ctaHref={pageData?.cta_link || "/contact"}
      />

      {/* Who It's For */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Who This Is For
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="text-4xl mb-4">👨‍💼</div>
              <h3 className="text-xl font-bold mb-3">Executives & Leaders</h3>
              <p className="text-gray-600">
                Senior leaders looking to enhance their strategic leadership and
                decision-making capabilities
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-xl font-bold mb-3">Entrepreneurs</h3>
              <p className="text-gray-600">
                Founders and business owners navigating growth challenges and
                leadership transitions
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-bold mb-3">
                Ambitious Professionals
              </h3>
              <p className="text-gray-600">
                High-potential individuals seeking career advancement and
                personal development
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Coaching Options */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Coaching Options
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coachingOptions.map((option, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <div className="p-8">
                  <div className="text-5xl mb-4 text-center">{option.icon}</div>
                  <h3 className="text-xl font-bold mb-3 text-center">
                    {option.title}
                  </h3>
                  <p className="text-gray-600 mb-6 text-center">
                    {option.description}
                  </p>

                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">What's Included:</h4>
                    <ul className="space-y-2">
                      {option.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">{option.duration}</span>
                      <Link
                        to={`/coaching/option/${option.id}`}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                      >
                        Learn More
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            What Clients Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-8">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">
                      ★
                    </span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mr-4">
                    <span className="text-gray-600 text-lg">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-bold">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Ready to Transform Your Career?
          </h2>
          <p className="mb-8 text-blue-100">
            Take the first step toward becoming the leader you aspire to be.
          </p>
          <div className="space-x-4">
            <Link
              to="/contact"
              className="inline-block bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
            >
              Apply for Coaching
            </Link>
            <Link
              to="/programs"
              className="inline-block border border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition"
            >
              View Training Programs
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CoachingPage;
